import { Customer, WithdrawalAccount, WithdrawalRequestWithAccountName } from '../../state/Radom'
import { getPaymentMethods } from '../../util/Managed'
import { shortAddress } from '../../util/Util'

/**
 * Destination-first model for the unified Send flow. Selecting a destination
 * derives the booking rails: the merchant's own saved accounts settle as
 * withdrawals, everything else (customers and one-off addresses) settles as
 * payouts.
 */

export type OneOffKind = 'evm' | 'solana' | 'tron' | 'iban' | 'other'

export type SendDestination =
  | { kind: 'ownAccount', account: WithdrawalAccount }
  | { kind: 'customer', customer: Customer }
  | { kind: 'oneOff', value: string, oneOffKind: OneOffKind }

export type SendMode = 'withdrawal' | 'payout'

export const destinationMode = (destination: SendDestination): SendMode => {
  if (destination.kind === 'ownAccount') return 'withdrawal'
  // Addresses outside the payout engine's networks save as wallet accounts
  // and settle on the withdrawal rail.
  if (destination.kind === 'oneOff' && destination.oneOffKind === 'other') return 'withdrawal'
  return 'payout'
}

export const EVM_ADDRESS_REGEX = /^0x[a-fA-F0-9]{40}$/
// Real Solana pubkeys encode to 43-44 base58 characters; the loose lower bound
// the chain allows (32) collides with legacy UTXO addresses, so stay strict.
export const SOLANA_ADDRESS_REGEX = /^[1-9A-HJ-NP-Za-km-z]{40,44}$/
export const TRON_ADDRESS_REGEX = /^T[1-9A-HJ-NP-Za-km-z]{33}$/
export const IBAN_REGEX = /^[A-Za-z]{2}\d{2}[A-Za-z0-9]{10,30}$/

/**
 * Address shapes for the withdrawal networks that aren't EVM, Solana or Tron.
 * A pasted address matching one of these becomes an 'other' one-off that
 * settles as a withdrawal to a saved wallet account (the only rail with full
 * network coverage). Legacy 1... addresses are valid on both Bitcoin forks,
 * so both networks stay candidates and the sender picks.
 */
const OTHER_NETWORK_ADDRESS_SHAPES: Array<{ network: string, regex: RegExp }> = [
  { network: 'Bitcoin', regex: /^(bc1[a-z0-9]{11,87}|[13][1-9A-HJ-NP-Za-km-z]{25,34})$/ },
  { network: 'BitcoinCash', regex: /^((bitcoincash:)?[qp][a-z0-9]{41}|[13][1-9A-HJ-NP-Za-km-z]{25,34})$/ },
  { network: 'Litecoin', regex: /^(ltc1[a-z0-9]{11,87}|[LM][1-9A-HJ-NP-Za-km-z]{26,33})$/ },
  { network: 'Dogecoin', regex: /^D[1-9A-HJ-NP-Za-km-z]{25,34}$/ },
  { network: 'Dash', regex: /^X[1-9A-HJ-NP-Za-km-z]{33}$/ },
  { network: 'Zcash', regex: /^t[13][1-9A-HJ-NP-Za-km-z]{33}$/ },
  { network: 'Cardano', regex: /^(addr1[a-z0-9]{50,110}|(Ae2|DdzFF)[1-9A-HJ-NP-Za-km-z]{40,110})$/ },
  { network: 'Polkadot', regex: /^1[1-9A-HJ-NP-Za-km-z]{44,50}$/ },
  { network: 'PolkadotAssetHub', regex: /^1[1-9A-HJ-NP-Za-km-z]{44,50}$/ }
]

/**
 * Networks whose address shape matches a pasted 'other' destination, limited
 * to networks the platform still offers (delisted chains are hidden or
 * disabled in the method registry and must not be offered as save targets).
 */
export const oneOffNetworkCandidates = (raw: string): string[] => {
  const value = normalizeOneOffInput(raw)
  const offeredNetworks = new Set(getPaymentMethods()
    .filter(method => !method.hostChain && !method.isDisabled && !method.isHidden && !method.isTestnet)
    .map(method => method.name))
  return OTHER_NETWORK_ADDRESS_SHAPES
    .filter(shape => offeredNetworks.has(shape.network))
    .filter(shape => shape.regex.test(value))
    .map(shape => shape.network)
}

export const normalizeOneOffInput = (raw: string): string => raw.trim().replace(/\s+/g, '')

/**
 * Networks a pasted destination could live on. One candidate means the
 * network is known; several means the person saving it has to choose.
 */
export const oneOffCandidateNetworks = (kind: OneOffKind, raw: string): string[] => {
  if (kind === 'iban') return []
  if (kind === 'other') return oneOffNetworkCandidates(raw)
  const names = new Set<string>()
  getPaymentMethods()
    .filter(method => !method.hostChain && !method.isDisabled && !method.isHidden && !method.isTestnet)
    .filter(method => networkAcceptsOneOffKind(kind, method.name, method.chainId))
    .forEach(method => names.add(method.name))
  return [...names]
}

/**
 * Detect whether free text pasted into the destination search is a sendable
 * one-off destination. Order matters: IBAN before Solana (country code + check
 * digits is the more specific shape), and the non-EVM chain shapes before
 * Solana so short legacy UTXO addresses aren't swallowed by base58 overlap.
 */
export const detectOneOffKind = (raw: string): OneOffKind | undefined => {
  const value = normalizeOneOffInput(raw)
  if (EVM_ADDRESS_REGEX.test(value)) return 'evm'
  if (IBAN_REGEX.test(value)) return 'iban'
  if (TRON_ADDRESS_REGEX.test(value)) return 'tron'
  if (oneOffNetworkCandidates(value).length > 0) return 'other'
  if (SOLANA_ADDRESS_REGEX.test(value)) return 'solana'
  return undefined
}

export const oneOffKindLabel = (kind: OneOffKind): string => {
  switch (kind) {
  case 'evm':
    return 'EVM address'
  case 'solana':
    return 'Solana address'
  case 'tron':
    return 'Tron address'
  case 'iban':
    return 'IBAN'
  case 'other':
    return 'Crypto address'
  }
}

/** Short human summary shown under a saved withdrawal account. */
export const withdrawalAccountSubtitle = (account: WithdrawalAccount): string => {
  const crypto = account.accountDetails.Crypto
  if (crypto) {
    return `${crypto.network} · ${shortAddress(crypto.address, 6)}`
  }

  const fiat = account.accountDetails.Fiat
  if (fiat?.accountType.iban) {
    return `EUR account · ${fiat.accountType.iban.iban}`
  }
  if (fiat?.accountType.us) {
    return `USD account · ${fiat.accountType.us.accountNumber}`
  }

  return 'Bank account'
}

export const customerDisplayName = (customer: Customer): string =>
  customer.name?.trim() || customer.companyName?.trim() || customer.email?.trim() || shortAddress(customer.id, 6)

/**
 * Stable hue (0-359) derived from a recipient's display name so the same
 * person always gets the same avatar color, without storing anything.
 */
export const nameHue = (seed: string): number => {
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0
  }
  return Math.abs(hash) % 360
}

/** Up-to-two-letter monogram for avatar circles ("Acme GmbH" → "AG"). */
export const avatarInitials = (name: string): string => {
  const cleaned = name.trim()
  if (!cleaned) return '·'
  if (/^0x/i.test(cleaned)) return cleaned.slice(2, 4).toUpperCase()
  // Words are stripped of leading punctuation so "Company (SEPA)" → "CS".
  const words = cleaned
    .split(/\s+/)
    .map(word => word.replace(/^[^0-9a-zA-ZÀ-ɏ]+/, ''))
    .filter(Boolean)
  if (words.length === 0) return cleaned.slice(0, 2).toUpperCase()
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return (words[0][0] + words[words.length - 1][0]).toUpperCase()
}

/** Identity key for deduping destinations (recent chips, lists). */
export const destinationKey = (destination: SendDestination): string => {
  switch (destination.kind) {
  case 'ownAccount':
    return `account:${destination.account.id}`
  case 'customer':
    return `customer:${destination.customer.id}`
  case 'oneOff':
    return `oneOff:${destination.value}`
  }
}

/** Human name for a selected destination, used in review and success copy. */
export const destinationDisplayName = (destination: SendDestination): string => {
  switch (destination.kind) {
  case 'ownAccount':
    return destination.account.name?.trim() || 'Withdrawal account'
  case 'customer':
    return customerDisplayName(destination.customer)
  case 'oneOff':
    return destination.oneOffKind === 'iban'
      ? destination.value
      : shortAddress(destination.value, 8)
  }
}

const EVM_NETWORK_NAMES = [
  'Ethereum',
  'SepoliaTestnet',
  'Base',
  'Arbitrum',
  'Polygon',
  'PolygonTestnet',
  'BNB',
  'BNBTestnet',
  'Avalanche'
]

/**
 * Whether a network can receive the given one-off address family. EVM chains
 * are recognised by chain id (with a name fallback for testnets that predate
 * chain-id metadata).
 */
export const networkAcceptsOneOffKind = (
  kind: OneOffKind,
  network: string,
  chainId?: number
): boolean => {
  switch (kind) {
  case 'evm':
    return !!chainId || EVM_NETWORK_NAMES.includes(network)
  case 'solana':
    return network === 'Solana' || network === 'SolanaDevnet' || network === 'SolanaTestnet'
  case 'tron':
    return network.startsWith('Tron')
  case 'iban':
  case 'other':
    // These settle through saved recipients/accounts, not one-off payouts.
    return false
  }
}

/** Address shape validation for batch rows against the chosen batch asset. */
export const addressMatchesNetwork = (
  address: string,
  network: string,
  chainId?: number
): boolean => {
  if (network.startsWith('Tron')) {
    return TRON_ADDRESS_REGEX.test(address)
  }
  if (network.startsWith('Solana')) {
    return SOLANA_ADDRESS_REGEX.test(address)
  }
  if (!!chainId || EVM_NETWORK_NAMES.includes(network)) {
    return EVM_ADDRESS_REGEX.test(address)
  }
  return false
}

export const formatQuoteAmount = (value?: number | string | null, decimals = 6): string => {
  if (value === undefined || value === null || value === '') return '-'
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return String(value)
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals
  })
}

export const getWithdrawalTransactionHash = (
  withdrawal: WithdrawalRequestWithAccountName
): string | undefined =>
  withdrawal.receipt?.EVMReceipt?.transactionHash ||
  withdrawal.receipt?.DOTReceipt?.transactionHash ||
  withdrawal.receipt?.BTCReceipt?.transactionId ||
  withdrawal.receipt?.SOLReceipt?.signature ||
  withdrawal.receipt?.FiatReceipt?.transactionHash

export const getWithdrawalNetworkFee = (
  withdrawal: WithdrawalRequestWithAccountName
): string | undefined =>
  withdrawal.receipt?.EVMReceipt?.networkFeeAmount ||
  withdrawal.receipt?.DOTReceipt?.networkFeeAmount ||
  withdrawal.receipt?.BTCReceipt?.networkFeeAmount ||
  withdrawal.receipt?.SOLReceipt?.networkFeeAmount
// ---------------------------------------------------------------------------
// Pricing a send that is funded from another balance
// ---------------------------------------------------------------------------

/**
 * How a cross-asset send is priced. A converted send sells the funding
 * balance for the delivered asset and pays the network fee on top of the
 * recipient amount; a stablecoin-funded send buys the delivered asset at the
 * exchange, whose withdrawal fee, spread, buffer and Radom's fee make up one
 * conversion cost, with no separate network fee.
 */
export type FundingKind = 'converted' | 'stablecoin'

export interface FundingQuotePoint {
  /** The recipient amount the quote was priced for, in the delivered asset. */
  recipient: number
  /** What the quote says the balance pays for it, in the funding asset. */
  funding: number
}

/**
 * The conversion rate a quote implies, in delivered units per funding unit,
 * with the separately listed cost taken out: the network fee of a converted
 * send (the sale covers the recipient amount plus the fee), the conversion
 * cost of a stablecoin-funded one (what is left buys the recipient amount at
 * the mid-market price).
 */
export const quotedConversionRate = (
  kind: FundingKind,
  point: FundingQuotePoint,
  networkFee: number,
  conversionCost: number
): number | undefined => {
  if (!(point.recipient > 0) || !(point.funding > 0)) return undefined
  const rate = kind === 'converted'
    ? (point.recipient + networkFee) / point.funding
    : point.recipient / (point.funding - conversionCost)
  return Number.isFinite(rate) && rate > 0 ? rate : undefined
}

/**
 * The recipient amount a typed funding amount buys at a fee-exclusive rate,
 * with the fixed cost taken out first and never below zero.
 */
export const recipientForFunding = (
  kind: FundingKind,
  pay: number,
  rate: number,
  networkFee: number,
  conversionCost: number
): number => {
  const recipient = kind === 'converted'
    ? pay * rate - networkFee
    : (pay - conversionCost) * rate
  return recipient > 0 ? recipient : 0
}

/** A quoted funding amount within this share of what was typed counts as matching it. */
export const FUNDING_MATCH_TOLERANCE = 0.0005

export const fundingMatchesTyped = (pay: number, funding: number): boolean =>
  pay > 0 && Math.abs(funding - pay) / pay <= FUNDING_MATCH_TOLERANCE

/**
 * The next recipient amount to ask for so that its quoted funding lands on
 * what was typed: a secant step across the two most recent quotes for this
 * typed amount, or a step along the rate while there is only one. Quotes
 * that move against the rate (a market jump between them) fall back to it.
 */
export const reconcileRecipientForFunding = (
  pay: number,
  points: FundingQuotePoint[],
  rate: number
): number | undefined => {
  const latest = points[points.length - 1]
  const previous = points[points.length - 2]
  if (!latest || !(rate > 0)) return undefined
  const secant = previous && previous.funding !== latest.funding
    ? (latest.recipient - previous.recipient) / (latest.funding - previous.funding)
    : undefined
  const slope = secant !== undefined && secant > 0 && secant < rate * 10 ? secant : rate
  const next = latest.recipient + (pay - latest.funding) * slope
  return Number.isFinite(next) ? Math.max(next, 0) : undefined
}
